'use client';

import {
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Paper,
  Popper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { SavedQuery } from '@repo/api-gw-sdk';
import { useMemo, useRef, useState } from 'react';

import { useDAL } from '@/contexts/useDAL';

interface SavedQueryPickerProps {
  workspaceId: string;
  onSelect: (query: string) => void;
}

export function SavedQueryPicker({
  workspaceId,
  onSelect,
}: SavedQueryPickerProps) {
  const dal = useDAL();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data } = dal.lakehouse.useListSavedQueries(workspaceId);
  const savedQueries: SavedQuery[] = useMemo(
    () => data?.savedQueries ?? [],
    [data]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return savedQueries;
    const term = search.toLowerCase();
    return savedQueries.filter(
      (q: SavedQuery) =>
        q.name.toLowerCase().includes(term) ||
        (q.description ?? '').toLowerCase().includes(term) ||
        q.tags.some((t) => t.toLowerCase().includes(term))
    );
  }, [savedQueries, search]);

  const handleSelect = (q: SavedQuery) => {
    onSelect(q.query);
    setOpen(false);
    setSearch('');
  };

  return (
    <>
      <Button
        ref={anchorRef}
        variant='outlined'
        size='small'
        onClick={() => setOpen((o) => !o)}
        sx={{ whiteSpace: 'nowrap' }}
        startIcon={
          <i
            className='material-symbols-folder-open-rounded'
            style={{ fontSize: 16 }}
          />
        }
      >
        Load Query
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement='bottom-start'
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper
            elevation={8}
            sx={{
              width: 600,
              maxHeight: 420,
              display: 'flex',
              flexDirection: 'column',
              mt: 0.5,
            }}
          >
            <Box
              sx={{
                p: 1.5,
                borderBottom: '1px solid var(--mui-palette-divider)',
              }}
            >
              <TextField
                placeholder='Search saved queries...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size='small'
                fullWidth
                autoFocus
                sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem' } }}
              />
            </Box>
            <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
              {filtered.length === 0 ? (
                <Stack
                  alignItems='center'
                  justifyContent='center'
                  sx={{ py: 4 }}
                >
                  <Typography variant='body2' color='text.secondary'>
                    {savedQueries.length === 0
                      ? 'No saved queries'
                      : 'No matching queries'}
                  </Typography>
                </Stack>
              ) : (
                <Table size='small'>
                  <TableBody>
                    {filtered.map((q: SavedQuery) => (
                      <TableRow
                        key={q.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleSelect(q)}
                      >
                        <TableCell>
                          <Typography
                            variant='body2'
                            noWrap
                            sx={{ maxWidth: 180, fontWeight: 500 }}
                          >
                            {q.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant='body2'
                            noWrap
                            sx={{ maxWidth: 220, color: 'text.secondary' }}
                          >
                            {q.description || '\u2014'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction='row' gap={0.5} flexWrap='wrap'>
                            {q.tags.length > 0 ? (
                              q.tags.map((tag) => (
                                <Chip
                                  key={tag}
                                  label={tag}
                                  size='small'
                                  variant='outlined'
                                />
                              ))
                            ) : (
                              <Typography
                                variant='caption'
                                color='text.secondary'
                              >
                                {'\u2014'}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
